import { EditOrderModal, TableAction } from '@/components';
import { useTranslation } from 'react-i18next';
import { StepProps } from './step2';
import { useEffect, useRef, useState } from 'react';
import userImportData from '@/hooks/useImportData';
import { Flex } from 'antd';
import { ButtonFilter } from '@/types';
import { useFetchPickupAddresses } from '@/hooksApi';
import { RefreshCw } from 'lucide-react';

const pageSize = 25;

const Step3 = ({ id, setRow, selectedPickupAddressId, needsRevalidation, onRevalidationStarted }: StepProps & { needsRevalidation?: boolean; onRevalidationStarted?: () => void }) => {
  const { t } = useTranslation();
  const [buttonFilter, setButtonFilter] = useState<ButtonFilter>(ButtonFilter.All);

  // pagination
  const [current, setCurrent] = useState(1);

  // Fetch pickup addresses to get region UID
  const { data: pickupAddressesData } = useFetchPickupAddresses();

  // Find the selected pickup address and get its region UID
  const selectedPickupAddress = pickupAddressesData?.object?.items?.find(
    (addr) => addr.uid === selectedPickupAddressId,
  );
  const pickupAddressRegionUid = selectedPickupAddress?.region?.uid;

  // get columns and data table
  const {
    columns,
    editingOrderUid,
    setEditingOrderUid,
    dataTables,
    loadingOrderExcel,
    total,
    invalid_rows_count,
    all_total,
    all_invalid_rows_count,
    all_no_carrier_count,
    handleRevalidate,
    isRevalidating,
  } = userImportData(
    String(id),
    current,
    buttonFilter,
    setCurrent,
    pageSize,
    pickupAddressRegionUid,
    selectedPickupAddressId,
  );

  useEffect(() => {
    setRow?.(invalid_rows_count);
  }, [invalid_rows_count, columns]);

  // Auto-revalidate when returning from Carrier Settings
  useEffect(() => {
    if (needsRevalidation && id) {
      handleRevalidate(() => {
        onRevalidationStarted?.();
      });
    }
  }, [needsRevalidation, id]);

  // Re-run carrier matching when merchant changes the pickup address dropdown.
  // Skip initial mount — the backend already resolves the correct address during upload.
  const prevPickupAddressId = useRef<string | undefined>(selectedPickupAddressId);
  useEffect(() => {
    if (selectedPickupAddressId && prevPickupAddressId.current && selectedPickupAddressId !== prevPickupAddressId.current) {
      handleRevalidate();
    }
    prevPickupAddressId.current = selectedPickupAddressId;
  }, [selectedPickupAddressId]);

  // handle onChange pagination
  const handlePaginationChange = (page: number) => {
    setCurrent(page);
  };

  // handle button filter
  const handleChangeButton = (buttonType: ButtonFilter) => {
    setCurrent(1);
    setButtonFilter(buttonType);
  };

  // Use persistent overall counts (from ALL data cache) for filter buttons
  // These don't change when switching tabs — they always reflect the total import
  const allValidCount = all_total > 0 ? all_total - all_invalid_rows_count : 0;

  const filterButtons: { type: ButtonFilter; label: string; count?: number }[] = [
    { type: ButtonFilter.All, label: t('all'), count: all_total || undefined },
    { type: ButtonFilter.VALID, label: t('valid'), count: allValidCount || undefined },
    { type: ButtonFilter.INVALID, label: t('invalid'), count: all_invalid_rows_count || undefined },
    { type: ButtonFilter.NO_CARRIER, label: t('no_carrier'), count: all_no_carrier_count || undefined },
  ];

  return (
    <Flex vertical>
      {/* Title row with filter buttons */}
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h2 className='text-base font-semibold text-sd-foreground mb-1'>
            {String(t('imported_orders'))}
          </h2>
          <p className='text-sm text-[#737b8c]'>
            {String(t('review_edit_before_finalizing'))}
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleRevalidate()}
            disabled={isRevalidating}
            className='inline-flex items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-xs font-medium transition-colors border border-solid border-sd-border cursor-pointer bg-white text-sd-foreground hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            <RefreshCw size={12} className={isRevalidating ? 'animate-spin' : ''} />
            {String(t('revalidate'))}
          </button>
          {filterButtons.map((btn) => (
            <button
              key={btn.type}
              onClick={() => handleChangeButton(btn.type)}
              className={`inline-flex items-center rounded-[10px] px-3 py-1.5 text-xs font-medium capitalize transition-colors border-none cursor-pointer ${
                buttonFilter === btn.type
                  ? 'bg-sd-primary text-sd-foreground'
                  : 'bg-[#f1f0ee] text-[#737b8c] hover:bg-gray-200'
              }`}
            >
              {btn.label}{btn.count !== undefined ? ` (${btn.count})` : ''}
            </button>
          ))}
        </div>
      </div>
      <TableAction
        columns={columns}
        data={dataTables}
        pageSize={pageSize}
        current={current}
        onPaginationChange={handlePaginationChange}
        total={total}
        loading={loadingOrderExcel}
        customClassName='import-table'
      />
      {/*
        RS Ph3 — QA bug fix: do NOT auto-revalidate the whole batch after a
        single-row edit. EditOrderForm already invalidates the import queries
        on success, and the backend PATCH already recalculates carriers for
        the edited row alone. The previous onSuccess={() => handleRevalidate()}
        cascaded the Rate Shopper default-engine pick onto every other row,
        wiping rule-driven attribution from rows that were never edited.
        Batch revalidate stays available via the explicit "Revalidate" button
        at the top of this page.
      */}
      <EditOrderModal
        editingOrderUid={editingOrderUid}
        onClose={() => setEditingOrderUid(undefined)}
      />
    </Flex>
  );
};

export default Step3;
